import { Platform } from 'react-native';
import { useEffect, useState, useRef, } from 'react';
import 'react-native-get-random-values';
import AppLoading from 'expo-app-loading';
import {
	LogBox,
	Text,
} from 'react-native';
import {
	NativeBaseProvider,
	extendTheme,
} from "native-base";
import {
	SET_CURRENT_SCREEN,
} from './src/models/actions/AppActions';
import {
	useFonts,
	Roboto_100Thin,
	Roboto_100Thin_Italic,
	Roboto_300Light,
	Roboto_300Light_Italic,
	Roboto_400Regular,
	Roboto_400Regular_Italic,
	Roboto_500Medium,
	Roboto_500Medium_Italic,
	Roboto_700Bold,
	Roboto_700Bold_Italic,
	Roboto_900Black,
	Roboto_900Black_Italic,
} from '@expo-google-fonts/roboto';
import {
	RobotoCondensed_300Light,
	RobotoCondensed_300Light_Italic,
	RobotoCondensed_400Regular,
	RobotoCondensed_400Regular_Italic,
	RobotoCondensed_700Bold,
	RobotoCondensed_700Bold_Italic,
} from '@expo-google-fonts/roboto-condensed';
import axios from 'axios';
import { Provider as ReduxProvider } from 'react-redux';
import { NavigationContainer } from '@react-navigation/native';
import useAppState from './src/OneHat/hooks/useAppState';
import handleAppStateChange from './src/models/Thunks/handleAppStateChange';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';

import oneHatData from '@onehat/data';
import AsyncStorageRepository from './src/OneHat/data/Repository/AsyncStorage';
import { logoutBecauseExpiredAuthentication } from './src/models/Thunks/setQuestion'; // I was getting circular dependency errors, so I combined lots of functions in this file
import SecureStore from './src/OneHat/data/Repository/SecureStore';
import allSchemas from './src/models/Schema/AllSchemas';
import Store from './src/models/Store'; // Redux

import Navigator from './src/Navigator';
import AppGlobals from './src/AppGlobals';
import ThemeOverrides from './src/styles/ThemeOverrides';
import _ from 'lodash';


// This is because we're getting warnings from NativeBase. Just ignore them for now.
if (LogBox) {
	LogBox.ignoreLogs([
		'A component changed from controlled to', // The AudioPlayer's scrubber does this. Ignore it, as it's intentional
		'contrast ratio',
		'VirtualizedLists should never be nested', // TODO: Remove when fixed
		'Non-serializable values were found in the navigation state',
		'Native splash screen is already hidden.',
	]);
}

export default function App() {
	
	const
		[isReady, setIsReady] = useState(false),
		routeNameRef = useRef(),
		navigationRef = useRef(),
		[fontsLoaded] = useFonts({
			Roboto_100Thin,
			Roboto_100Thin_Italic,
			Roboto_300Light,
			Roboto_300Light_Italic,
			Roboto_400Regular,
			Roboto_400Regular_Italic,
			Roboto_500Medium,
			Roboto_500Medium_Italic,
			Roboto_700Bold,
			Roboto_700Bold_Italic,
			Roboto_900Black,
			Roboto_900Black_Italic,
			RobotoCondensed_300Light,
			RobotoCondensed_300Light_Italic,
			RobotoCondensed_400Regular,
			RobotoCondensed_400Regular_Italic,
			RobotoCondensed_700Bold,
			RobotoCondensed_700Bold_Italic,
		}),
		onNavReady = () => {
			routeNameRef.current = navigationRef.current && navigationRef.current.getCurrentRoute && navigationRef.current.getCurrentRoute().name;
		},
		onNavStateChange = () => {
			const
				previous = routeNameRef.current,
				current = navigationRef.current.getCurrentRoute().name;

			if (previous !== current) {
				Store.dispatch({
					type: SET_CURRENT_SCREEN,
					currentScreen: current,
				});
			}

			routeNameRef.current = current;
		},
		setupOneHatData = async () => {
			if (oneHatData.hasSchemaWithName('CommandQueue')) {
				return;
			}

			axios.defaults.withCredentials = false; // Don't use cookies in Ajax requests. We'll supply a header token instead.

			return oneHatData
						.setRepositoryGlobals({
							debugMode: AppGlobals.debugMode,
							api: {
								baseURL: AppGlobals.baseURL,
							},
							timeout: 15000,
							pageSize: 20,
						})
						.registerRepositoryTypes([
							AsyncStorageRepository,
							SecureStore,
						])
						.createSchemas(_.map(allSchemas, Schema => Schema))
						.createBoundRepositories()
						.then(async (oneHatData) => {
							if (AppGlobals.clearAsync) {
								const asyncRepositories = oneHatData.getRepositoriesByType('async');
								let i, repository;
								for (i = 0; i < asyncRepositories.length; i++) {
									repository = asyncRepositories[i];
									if (repository.name !== 'Prefs') {
										await repository.clearAll();
									}
								}
							}
							if (AppGlobals.clearEvents) {
								const Events = oneHatData.getRepository('Events');
								await Events.clearAll();
							}
							if (AppGlobals.clearSecure) {
								const secureRepositories = oneHatData.getRepositoriesByType('secure'),
									Secure = secureRepositories[0];
								await Secure.clearAll();
							}

							const oneBuildRepositories = oneHatData.getRepositoriesByType('onebuild');
							_.each(oneBuildRepositories, (repository) => {
								repository.on('logout', logoutBecauseExpiredAuthentication);
							});
						});
		};

	useEffect(() => {
		(async () => {
			await setupOneHatData();

			setIsReady(true);
		})();
	}, []);

	if (Platform.OS === 'ios' || Platform.OS === 'android') {
		// Don't use this on web
		useAppState({
			onChange: (appState) => {
				Store.dispatch(handleAppStateChange(appState));
			},
		});
	}

	if (!isReady || !fontsLoaded) {
		return <AppLoading />;
	}

	const nbConfig = {
		strictMode: 'off',
	};
	
	return <ReduxProvider store={Store}>
				<ActionSheetProvider>
					<NativeBaseProvider config={nbConfig} theme={extendTheme(ThemeOverrides)}>
						<NavigationContainer ref={navigationRef} onReady={onNavReady} onStateChange={onNavStateChange}>
							<Navigator />
						</NavigationContainer>
					</NativeBaseProvider>
				</ActionSheetProvider>
			</ReduxProvider>;
}
